import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Layout } from "@/components/Layout";
import { AuditLog } from "@/components/AuditLog";
import NotFound from "@/pages/not-found";
import CreateRulePrompt from "./components/AIBuilder/CreateRulePrompt";
import { useAuthMe } from "./hooks/useLocalStorage";
import { useEffect } from "react";
import { PortalForms } from "./components/FormDesigner/PortalForms";
import { BreadcrumbProvider } from "./components/BreadcrumbContext";
import { RuleVersionHistory } from "./components/VersionRule/RuleVersionHistory";
import { RedirectingScreen } from "./components/authentication/LoadingSpinner";


function Router() {
  return (
    <BreadcrumbProvider>

      <Layout>
        <Switch>
          <Route path="/">
            <PortalForms fromType="PortalForm" formRoute="/" />
          </Route>
          <Route path="/workflowForms">
            <PortalForms fromType="WorkflowForm" formRoute="workflowForms" />
          </Route>
          <Route path="/partnerWorkflowForm">
            <PortalForms fromType="PartnerForm" formRoute="partnerWorkflowForm" />
          </Route>
          <Route path="/adhocForm">
            <PortalForms fromType="AdhocForm" formRoute="adhocForm" />
          </Route>
          <Route path="/libraryForm">
            <PortalForms fromType="LibraryForm" formRoute="libraryForm" />
          </Route>
          <Route path="/lettertemplate">
            <PortalForms fromType="LetterTemplate" formRoute="lettertemplate" />
          </Route>
          <Route path="/emailtemplate">
            <PortalForms fromType="EmailTemplate" formRoute="emailtemplate" />
          </Route>
          <Route path="/surveytemplate">
            <PortalForms fromType="SurveyTemplate" formRoute="surveytemplate" />
          </Route>
          <Route path="/subformstemplate">
            <PortalForms fromType="SubForm" formRoute="subformstemplate" />
          </Route>
          <Route path="/defaultOffers">
            <PortalForms fromType="DefaultOffer" formRoute="defaultOffers" />
          </Route>
          <Route path="/additionaldefaultOffers">
            <PortalForms fromType="AdditionalOffer" formRoute="additionaldefaultOffers" />
          </Route>
          <Route path="ruleversion/:ruleId/:name" component={RuleVersionHistory} />
          <Route path="/audit" component={AuditLog} />
          <Route path="/ai-builder" component={CreateRulePrompt} />
          <Route component={NotFound} />
        </Switch>
      </Layout>
    </BreadcrumbProvider>
  );
}

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { data, isLoading, isError } = useAuthMe();

  useEffect(() => {
    if (isError) {
      console.log("Authentication check failed");
    }
  }, [isError]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">redirecting...</p>
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return <RedirectingScreen />;
  }

  return <>{children}</>;
}

function App() {
  useAuthMe();
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <AuthGuard>
          <Router />
        </AuthGuard>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;

